import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";

import { getDvSourceImageUrl } from "../../../functions/themeAssets";

const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg"];
const MAX_FILE_BYTES = 5 * 1024 * 1024;

/** Same fields and limits as old/admin/themeUpdate.php's #uploadFormgame. */
const SLOTS = [
  { key: "game_desk", label: "Desktop background image", fieldName: "game_desk Upload", width: 1920, height: 1080 },
  { key: "game_mob", label: "Mobile background image", fieldName: "game_mob Upload", width: 414, height: 896 },
];

function readImageSize(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("load"));
    };
    img.src = url;
  });
}

async function validateSlotFile(file, slot) {
  if (!file) return `${slot.fieldName} is required.`;
  if (!ALLOWED_TYPES.includes(file.type)) return `${slot.fieldName} must be a PNG, JPG, JPEG file.`;
  if (file.size > MAX_FILE_BYTES) return `${slot.fieldName} must not exceed 5 MB.`;
  try {
    const { width, height } = await readImageSize(file);
    if (width !== slot.width || height !== slot.height) {
      return `${slot.fieldName} must have dimensions ${slot.width}x${slot.height}.`;
    }
  } catch {
    return `Failed to load ${slot.fieldName} for dimension validation.`;
  }
  return "";
}

export default function GamePageImageTab({ themeData, currentTheme, adminToken, onUploaded }) {
  const [files, setFiles] = useState({});
  const [previews, setPreviews] = useState({});
  const [uploading, setUploading] = useState(false);

  const previewsRef = useRef(previews);
  previewsRef.current = previews;
  useEffect(
    () => () => Object.values(previewsRef.current).forEach((url) => URL.revokeObjectURL(url)),
    []
  );

  const pickFile = (key, file) => {
    setFiles((prev) => ({ ...prev, [key]: file || undefined }));
    setPreviews((prev) => {
      const next = { ...prev };
      if (prev[key]) URL.revokeObjectURL(prev[key]);
      if (file) next[key] = URL.createObjectURL(file);
      else delete next[key];
      return next;
    });
  };

  const clearPreviews = () => {
    Object.values(previewsRef.current).forEach((url) => URL.revokeObjectURL(url));
    setPreviews({});
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    for (const slot of SLOTS) {
      const error = await validateSlotFile(files[slot.key], slot);
      if (error) {
        Swal.fire({ icon: "error", title: error });
        return;
      }
    }

    const formData = new FormData();
    formData.append("themeName", currentTheme);
    SLOTS.forEach((slot) => formData.append(slot.key, files[slot.key]));

    setUploading(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/updateThemeMedia`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: formData,
      });
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(result.error || "Upload failed");
      await onUploaded?.();
      setFiles({});
      clearPreviews();
      Swal.fire({ icon: "success", title: "Data Updated", timer: 1000, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: "error", title: err.message || "Upload failed" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <form className="themeupdate-panel-wide se-image-panel" onSubmit={handleUpload}>
      <h2 className="themeupdate-card-title">Add Game Page Image</h2>

      <div className="se-image-grid">
        {SLOTS.map((slot) => {
          const current = themeData?.[slot.key] ? getDvSourceImageUrl(themeData[slot.key]) : "";
          const preview = previews[slot.key] || current;
          return (
            <div className="se-image-slot" key={slot.key}>
              <p className="se-image-label">{slot.label}</p>
              <label className="sl-file-label">
                <i className="fa-solid fa-image" aria-hidden />
                <span>{files[slot.key]?.name || "Choose file"}</span>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={(ev) => pickFile(slot.key, ev.target.files?.[0])}
                  disabled={uploading}
                />
              </label>
              <p className="rules-field-hint">
                <code>Allowed photo formats: JPG, JPEG and PNG.</code>
                <code>
                  Image dimensions: {slot.width} x {slot.height} px.
                </code>
              </p>
              <div className={`se-image-preview se-image-preview--${slot.key}`}>
                {preview ? <img src={preview} alt={slot.label} /> : <span>No image</span>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="se-image-actions">
        <button type="submit" className="save-and-continue" disabled={uploading}>
          {uploading ? "Uploading..." : "Upload"}
        </button>
      </div>
    </form>
  );
}
